/**
 * THE LIGHT OF THE PAGE UNDER THE APP HEADER (PV15, 2026-09-30).
 *
 * A page of the new design in the app frame (chrome 'app': /desktop first) stands under the app header, which App.tsx
 * draws, outside the page. Over today's pages that header is noon and offers no light switch (D-66: today's pages are
 * noon only). Over a page of the new design it must take that page's light and offer the page's switch, the one the
 * public header carries, or a dusk page would stand under a noon band with no way to change it.
 *
 * So the frame of an 'app' page PUBLISHES its light here while it is mounted (the theme it is drawn in, the stored
 * choice, and the function that changes it), and the app header reads it: absent, the header takes the light under it
 * (noon over today's pages).
 *
 * THE DOOR PUBLISHES IT TOO (D-92, Miles 2026-10-24: between two dusk pages "it goes light mode blank before loading"):
 * while an 'app' page's chunks arrive, its door (./withPavilion.tsx) publishes the light it paints in, and both publish
 * in a layout effect, so the release of the page left and the publish of the page arrived land in one commit and the
 * band never shows noon between them. So this module is on the first download, with the door; the shell's frame and
 * the app header's chunk read the same copy.
 */
import { useSyncExternalStore } from 'react';
import type { PreviewTheme, PreviewThemeChoice } from '../previewTheme';

export interface AppPageLight {
  /** The light the page is drawn in. */
  theme: PreviewTheme;
  /** The visitor's stored choice (noon, dusk or their device's). */
  choice: PreviewThemeChoice;
  /** Changes the choice; the page redraws in the new light and publishes it again. */
  choose: (choice: PreviewThemeChoice) => void;
}

let current: AppPageLight | null = null;
let token = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of [...listeners]) l();
}

/** Publish the page's light; returns its release, which clears it only if nothing newer was published since. */
export function publishAppPageLight(light: AppPageLight): () => void {
  token += 1;
  const mine = token;
  current = light;
  emit();
  return () => {
    if (token !== mine) return;
    current = null;
    emit();
  };
}

export function readAppPageLight(): AppPageLight | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The light of the new-design page under the app header, or null over today's pages. */
export function useAppPageLight(): AppPageLight | null {
  return useSyncExternalStore(subscribe, readAppPageLight, () => null);
}
